/** Merge transient capture placeholders with durable session projections.
 *
 * A session with `has_log === false` owns a grid even after it reaches a
 * terminal status. Once any widget row has existed (`has_log === true`) the
 * real row, or the user's deletion of it, supersedes the pending projection.
 */
export function mergePendingGridAdds(localAdds = [], sessions = [], scope = {}) {
  const loggedSessionIds = new Set();
  const loggedGridIds = new Set();
  const durable = [];

  for (const session of sessions || []) {
    const sessionId = String(session?.id || session?.session_id || '').trim();
    const gridId = String(session?.grid_id || session?.client_ref || sessionId).trim();
    if (session?.has_log === true) {
      if (sessionId) loggedSessionIds.add(sessionId);
      if (gridId) loggedGridIds.add(gridId);
      continue;
    }
    // Undefined means an older server that does not expose the projection
    // contract yet; don't reinterpret every historical session as pending.
    const boundWidget = String(session?.widget_id || '').trim();
    const boundInstance = String(session?.widget_instance_id || '').trim();
    const currentWidget = String(scope?.widgetId || '').trim();
    const currentInstance = String(scope?.instanceId || '').trim();
    if (session?.has_log !== false || session?.is_widget_bound !== true || !sessionId || !gridId) continue;
    if (!boundWidget || !boundInstance) continue;
    if ((currentWidget && boundWidget !== currentWidget) || (currentInstance && boundInstance !== currentInstance)) continue;
    const created = Date.parse(session.created_at || session.started_at || '');
    durable.push({
      client_ref: gridId,
      session_id: sessionId,
      title: session.title || 'Pending',
      started_at: Number.isFinite(created) ? created : Date.now(),
      ...(session.widget_id ? { widget_id: session.widget_id } : {}),
      ...(session.widget_instance_id ? { widget_instance_id: session.widget_instance_id } : {}),
      ...(session.thumb ? { thumb: session.thumb } : {}),
    });
  }

  const merged = new Map();
  for (const add of localAdds || []) {
    const gridId = String(add?.client_ref || '').trim();
    const sessionId = String(add?.session_id || '').trim();
    const boundWidget = String(add?.widget_id || '').trim();
    const boundInstance = String(add?.widget_instance_id || '').trim();
    const currentWidget = String(scope?.widgetId || '').trim();
    const currentInstance = String(scope?.instanceId || '').trim();
    if (!gridId || loggedGridIds.has(gridId) || (sessionId && loggedSessionIds.has(sessionId))) continue;
    if ((currentWidget && boundWidget !== currentWidget) || (currentInstance && boundInstance !== currentInstance)) continue;
    merged.set(gridId, add);
  }
  for (const add of durable) {
    const previous = merged.get(add.client_ref);
    merged.set(add.client_ref, previous ? { ...add, ...previous, session_id: add.session_id } : add);
  }
  return [...merged.values()];
}
